import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { Loader2, Mail, ScrollText, TrendingUp, Wrench } from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

type Severity = "info" | "warning" | "critical";

type Step = "idle" | "counting" | "confirming" | "sending";

type PendingSend = {
  fnName: string;
  payload: Record<string, unknown>;
  count: number;
  label: string;
};

const LegalNotifications = () => {
  // ─── Form 1: Price change ───────────────────────────
  const [pcEffectiveDate, setPcEffectiveDate] = useState("");
  const [pcPlanName, setPcPlanName] = useState("");
  const [pcOldPrice, setPcOldPrice] = useState("");
  const [pcNewPrice, setPcNewPrice] = useState("");
  const [pcReason, setPcReason] = useState("");

  // ─── Form 2: Terms update ───────────────────────────
  const [tuEffectiveDate, setTuEffectiveDate] = useState("");
  const [tuChanges, setTuChanges] = useState("");

  // ─── Form 3: Service announcement ──────────────────
  const [saTitle, setSaTitle] = useState("");
  const [saBody, setSaBody] = useState("");
  const [saSeverity, setSaSeverity] = useState<Severity>("info");

  // ─── Confirmation flow ──────────────────────────────
  const [step, setStep] = useState<Step>("idle");
  const [pending, setPending] = useState<PendingSend | null>(null);

  const askConfirm = async (
    fnName: string,
    label: string,
    payload: Record<string, unknown>,
  ) => {
    setStep("counting");
    try {
      const { data, error } = await supabase.functions.invoke<{
        recipient_count?: number;
        error?: string;
      }>(fnName, { body: { ...payload, dryRun: true } });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const count = data?.recipient_count ?? 0;
      if (count === 0) {
        toast.warning("Nu există destinatari eligibili pentru această notificare.");
        setStep("idle");
        return;
      }

      setPending({ fnName, payload, count, label });
      setStep("confirming");
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Eroare la verificare";
      toast.error(msg);
      setStep("idle");
    }
  };

  const confirmSend = async () => {
    if (!pending) return;
    setStep("sending");
    try {
      const { data, error } = await supabase.functions.invoke<{
        emails_sent?: number;
        errors_count?: number;
        error?: string;
      }>(pending.fnName, { body: pending.payload });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      const sent = data?.emails_sent ?? 0;
      const failed = data?.errors_count ?? 0;
      if (failed > 0) {
        toast.warning(`Trimise: ${sent} · Eșuate: ${failed}`);
      } else {
        toast.success(`✅ Email trimis cu succes la ${sent} utilizatori.`);
      }
    } catch (err: unknown) {
      const msg = err instanceof Error ? err.message : "Eroare la trimitere";
      toast.error(msg);
    } finally {
      setPending(null);
      setStep("idle");
    }
  };

  const isBusy = step !== "idle";

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-primary/30 bg-primary/5 p-4 text-sm">
        <p className="font-medium text-foreground mb-1">⚠️ Notificări către toți utilizatorii</p>
        <p className="text-muted-foreground leading-relaxed">
          Aceste butoane trimit emailuri către <strong>toți utilizatorii activi</strong> (exclusiv conturile programate pentru ștergere).
          Vei vedea numărul exact de destinatari și o confirmare înainte de trimiterea efectivă.
        </p>
      </div>

      {/* ─── PRICE CHANGE ─────────────────────────── */}
      <section className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <TrendingUp className="h-5 w-5 text-primary" />
          <h3 className="font-heading font-semibold">Notificare modificare prețuri</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Conform <strong>Termenilor §4</strong>, utilizatorii trebuie anunțați cu cel puțin <strong>30 de zile înainte</strong> de orice modificare a prețului.
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label htmlFor="pc-plan">Plan afectat</Label>
            <Input
              id="pc-plan"
              placeholder="ex: Profesional"
              value={pcPlanName}
              onChange={(e) => setPcPlanName(e.target.value)}
              disabled={isBusy}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pc-date">Data intrării în vigoare</Label>
            <Input
              id="pc-date"
              placeholder="ex: 15 mai 2026"
              value={pcEffectiveDate}
              onChange={(e) => setPcEffectiveDate(e.target.value)}
              disabled={isBusy}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pc-old">Preț actual</Label>
            <Input
              id="pc-old"
              placeholder="ex: 29 RON/lună"
              value={pcOldPrice}
              onChange={(e) => setPcOldPrice(e.target.value)}
              disabled={isBusy}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="pc-new">Preț nou</Label>
            <Input
              id="pc-new"
              placeholder="ex: 35 RON/lună"
              value={pcNewPrice}
              onChange={(e) => setPcNewPrice(e.target.value)}
              disabled={isBusy}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="pc-reason">Motiv (opțional)</Label>
          <Textarea
            id="pc-reason"
            placeholder="ex: Ajustare în linie cu inflația..."
            value={pcReason}
            onChange={(e) => setPcReason(e.target.value)}
            disabled={isBusy}
            rows={2}
          />
        </div>
        <Button
          onClick={() =>
            askConfirm("notify-price-change", "Notificare modificare prețuri", {
              effectiveDate: pcEffectiveDate,
              planName: pcPlanName,
              oldPrice: pcOldPrice,
              newPrice: pcNewPrice,
              reason: pcReason,
            })
          }
          disabled={isBusy || !pcEffectiveDate || !pcPlanName}
          className="w-full sm:w-auto"
        >
          {step === "counting" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
          Trimite notificarea
        </Button>
      </section>

      {/* ─── TERMS UPDATE ─────────────────────────── */}
      <section className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <ScrollText className="h-5 w-5 text-primary" />
          <h3 className="font-heading font-semibold">Notificare actualizare T&amp;C</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Conform <strong>Termenilor §12</strong>, modificările T&amp;C trebuie comunicate cu cel puțin <strong>15 zile înainte</strong> de intrarea în vigoare.
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="tu-date">Data intrării în vigoare</Label>
          <Input
            id="tu-date"
            placeholder="ex: 1 mai 2026"
            value={tuEffectiveDate}
            onChange={(e) => setTuEffectiveDate(e.target.value)}
            disabled={isBusy}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tu-changes">Descriere modificări (opțional)</Label>
          <Textarea
            id="tu-changes"
            placeholder="ex: Am actualizat secțiunile privind politica de confidențialitate..."
            value={tuChanges}
            onChange={(e) => setTuChanges(e.target.value)}
            disabled={isBusy}
            rows={3}
          />
        </div>
        <Button
          onClick={() =>
            askConfirm("notify-terms-update", "Notificare actualizare T&C", {
              effectiveDate: tuEffectiveDate,
              changesDescription: tuChanges,
            })
          }
          disabled={isBusy}
          className="w-full sm:w-auto"
        >
          {step === "counting" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
          Trimite notificarea
        </Button>
      </section>

      {/* ─── SERVICE ANNOUNCEMENT ─────────────────── */}
      <section className="rounded-xl border border-border bg-card p-5 space-y-4">
        <div className="flex items-center gap-2">
          <Wrench className="h-5 w-5 text-primary" />
          <h3 className="font-heading font-semibold">Anunț de serviciu</h3>
        </div>
        <p className="text-xs text-muted-foreground">
          Folosit pentru mentenanță programată, downtime, breach-uri de securitate sau alte comunicări legate de Serviciu (<strong>Privacy §3</strong>).
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="sa-title">Titlu *</Label>
          <Input
            id="sa-title"
            placeholder="ex: Mentenanță programată — 5 mai 2026"
            value={saTitle}
            onChange={(e) => setSaTitle(e.target.value)}
            disabled={isBusy}
            maxLength={120}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sa-body">Conținut * (un paragraf per rând)</Label>
          <Textarea
            id="sa-body"
            placeholder="Te anunțăm că pe data de... între orele... EZ Factura va fi indisponibil..."
            value={saBody}
            onChange={(e) => setSaBody(e.target.value)}
            disabled={isBusy}
            rows={5}
            maxLength={2000}
          />
          <p className="text-xs text-muted-foreground">{saBody.length}/2000</p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="sa-severity">Nivel de urgență</Label>
          <Select value={saSeverity} onValueChange={(v) => setSaSeverity(v as Severity)} disabled={isBusy}>
            <SelectTrigger id="sa-severity">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="info">ℹ️ Informare — mentenanță plănuită, update-uri</SelectItem>
              <SelectItem value="warning">⚠️ Atenție — degradare serviciu, downtime neașteptat</SelectItem>
              <SelectItem value="critical">🚨 Urgent — breach de securitate, incident major</SelectItem>
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Schimbă culoarea bannerului și prefix-ul subiectului din email.
          </p>
        </div>
        <Button
          onClick={() =>
            askConfirm("notify-service-announcement", "Anunț de serviciu", {
              title: saTitle,
              body: saBody,
              severity: saSeverity,
            })
          }
          disabled={isBusy || saTitle.trim().length < 3 || saBody.trim().length < 10}
          className="w-full sm:w-auto"
        >
          {step === "counting" ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
          Trimite anunțul
        </Button>
      </section>

      {/* ─── CONFIRM DIALOG ───────────────────────── */}
      <AlertDialog
        open={step === "confirming" || step === "sending"}
        onOpenChange={(open) => {
          if (!open && step === "confirming") {
            setPending(null);
            setStep("idle");
          }
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Confirmă trimiterea</AlertDialogTitle>
            <AlertDialogDescription>
              Vei trimite „<strong>{pending?.label}</strong>" către{" "}
              <strong className="text-foreground">{pending?.count} utilizatori</strong>.
              <br /><br />
              Această acțiune este <strong>ireversibilă</strong>. Emailurile vor pleca imediat și nu pot fi retrase.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={step === "sending"}>Anulează</AlertDialogCancel>
            <AlertDialogAction
              disabled={step === "sending"}
              onClick={(e) => {
                e.preventDefault();
                confirmSend();
              }}
            >
              {step === "sending" ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Se trimite...
                </>
              ) : (
                `Da, trimite la ${pending?.count} utilizatori`
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default LegalNotifications;
